import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { useChat } from '../../hooks/useChat';
import { useFetchConversationById } from '../../hooks/useFetchConversationById';
import { useFileUpload, type FileReference } from '../../hooks/useFileUpload';
import { useChatResume } from '../../hooks/useChatResume';
import { useChatStore, type PendingSeed } from '../../stores/chatStore';
import { useAuthStore } from '../../stores/authStore';
import { ArtifactPanel } from './components/ArtifactPanel';
import { ChatDropOverlay } from './components/ChatDropOverlay';
import { ChatInput } from './components/ChatInput';
import { ChatMessages } from './components/ChatMessages';
import { ThreadSidebar } from './components/ThreadSidebar';
import { extractArtifacts, mapConversationMessage } from './conversationMapper';

export function ChatPage() {
  const navigate = useNavigate();
  const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>();
  const conversationId = urlConversationId ?? null;

  const { messages, status, isStreaming, sendMessage } = useChat();
  const activeArtifactIndex = useChatStore((s) => s.activeArtifactIndex);
  const pendingSeed = useChatStore((s) => s.pendingSeed);
  const hydratedForConvId = useChatStore((s) => s.hydratedForConvId);
  const isHydrated = conversationId !== null && hydratedForConvId === conversationId;

  useChatResume(conversationId);

  const { data, isLoading, isError } = useFetchConversationById(conversationId);

  // URL → store sync. Clear local state on conversation switch so the new URL
  // renders as a cold load (skeleton + server fetch). If the store already
  // matches the URL (e.g. useChat just created and streamed into it, then
  // navigated), preserve its messages — don't wipe and don't re-fetch.
  useEffect(() => {
    const store = useChatStore.getState();
    if (!conversationId) {
      store.reset();
      return;
    }
    if (store.conversationId !== conversationId) {
      store.prepareForConversation(conversationId);
    }
  }, [conversationId]);

  // Populate the store from the server's fetched data on cold load only.
  // When the store is already hydrated for this conversation — either by a
  // prior server fetch, or by useChat marking it after creating + streaming
  // into it — skip. This prevents polling and the post-stream cache refresh
  // from wiping the just-streamed exchange when the backend hasn't yet
  // persisted it.
  useEffect(() => {
    if (!data || !conversationId) return;
    if (isStreaming) return;
    if (data.id !== conversationId) return;
    if (useChatStore.getState().hydratedForConvId === conversationId) return;
    const chatMessages = (data.messages ?? []).map(mapConversationMessage);
    const artifacts = extractArtifacts(data.messages ?? []);
    useChatStore.getState().loadConversation(conversationId, chatMessages, artifacts);
  }, [data, conversationId, isStreaming]);

  // One-shot per pendingSeed object: fire sendMessage once and never again
  // for the same seed. Gated on isHydrated so the seed fires only AFTER
  // server history has been loaded into the store (handles the stale-cache-hit
  // case where isLoading is false but loadConversation hasn't run yet).
  const firedForSeedRef = useRef<PendingSeed | null>(null);
  useEffect(() => {
    if (!pendingSeed) return;
    if (firedForSeedRef.current === pendingSeed) return;
    if (!conversationId) return;
    if (pendingSeed.conversationId !== conversationId) return;
    if (!isHydrated) return;
    if (isStreaming) return;

    firedForSeedRef.current = pendingSeed;
    const files: FileReference[] | undefined = pendingSeed.attachments.map((a) => ({
      uploadId: a.documentId ?? a.fileKey,
      documentId: a.documentId,
      fileKey: a.fileKey,
      fileName: a.fileName,
      fileType: a.fileType,
      fileUrl: a.fileUrl,
    }));
    const content = pendingSeed.content;
    useChatStore.getState().setPendingSeed(null);
    sendMessage(content, files);
  }, [pendingSeed, conversationId, isHydrated, isStreaming, sendMessage]);

  const { files, addFiles, removeFile, clearFiles, pendingFiles, isUploading } = useFileUpload({
    linkedConversationId: conversationId,
  });

  const MAX_FILES = 5;
  const remaining = Math.max(0, MAX_FILES - files.length);

  const handleDropAccepted = useCallback(
    (accepted: File[], rejectedCount: number) => {
      const toAdd = accepted.slice(0, remaining);
      const overCap = accepted.length - toAdd.length;

      if (toAdd.length > 0) {
        addFiles(toAdd);
      }

      const parts: string[] = [];
      if (toAdd.length > 0) parts.push(`${toAdd.length} added`);
      if (overCap > 0) parts.push(`${overCap} skipped (max ${MAX_FILES})`);
      if (rejectedCount > 0) parts.push(`${rejectedCount} skipped (unsupported type)`);
      if (parts.length > 0) {
        const hasSkip = overCap > 0 || rejectedCount > 0;
        const message = parts.join(' · ');
        if (hasSkip) toast.warning(message);
        else if (toAdd.length > 0) toast.success(message);
      }
    },
    [addFiles, remaining]
  );

  const role = useAuthStore((s) => s.role);
  const headerStatus = data?.status ?? null;
  // Read-only members (the default invited role) can view but not write. The
  // backend also 403s their writes, but gate the composer/upload here so the UX
  // is honest instead of click-then-error (backlog A3).
  const isReadOnly = role === 'MEMBER';
  const inputDisabled =
    isStreaming || isReadOnly || headerStatus === 'RESOLVED' || headerStatus === 'ARCHIVED';

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled: inputDisabled,
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/heic': ['.heic'],
    },
    onDrop: (accepted, rejected) => handleDropAccepted(accepted, rejected.length),
  });

  // Error state: invalid / 404 conversation id in URL.
  if (conversationId && isError) {
    return (
      <div className="flex h-full">
        <ThreadSidebar />
        <div className="flex-1 flex flex-col items-center justify-center bg-paper gap-3">
          <p className="text-sm text-surface-700 font-medium">Conversation not found.</p>
          <button
            type="button"
            onClick={() => navigate('/chat')}
            className="px-3 py-1.5 rounded-md border border-surface-300 text-xs hover:border-surface-500"
          >
            Back to conversations
          </button>
        </div>
      </div>
    );
  }

  // Full-page skeleton only on cold load. If the store is already hydrated
  // for this conversation (post-[DONE] URL sync, or just-streamed), skip
  // the skeleton so the URL change is invisible to the user.
  if (conversationId && isLoading && !isHydrated) {
    return (
      <div className="flex h-full">
        <ThreadSidebar />
        <div className="flex-1 flex flex-col min-w-0 bg-paper">
          <div className="h-14 border-b border-surface-200 animate-pulse bg-surface-50" />
          <div className="flex-1 p-6 space-y-3">
            <div className="h-4 w-2/3 bg-surface-100 rounded animate-pulse" />
            <div className="h-4 w-1/2 bg-surface-100 rounded animate-pulse" />
            <div className="h-4 w-3/4 bg-surface-100 rounded animate-pulse" />
          </div>
          <div className="h-20 border-t border-surface-200 animate-pulse bg-surface-50" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <ThreadSidebar />
      <div
        {...getRootProps({
          className: 'flex-1 flex flex-col min-w-0 bg-surface-50 relative',
          onClick: (e) => e.stopPropagation(),
        })}
      >
        <input {...getInputProps()} />
        <ChatDropOverlay visible={isDragActive && !inputDisabled} remainingSlots={remaining} />
        <ChatMessages messages={messages} status={status} />
        <ChatInput
          onSend={(msg, fileRefs) => sendMessage(msg, fileRefs)}
          disabled={inputDisabled}
          disabledReason={
            isReadOnly
              ? 'Your access is read-only. Ask an owner or admin to make changes.'
              : headerStatus === 'RESOLVED' || headerStatus === 'ARCHIVED'
                ? 'This conversation is closed. Start a new one to continue.'
                : undefined
          }
          files={files}
          pendingFiles={pendingFiles}
          isUploading={isUploading}
          removeFile={removeFile}
          clearFiles={clearFiles}
          openFilePicker={open}
        />
      </div>
      {activeArtifactIndex !== null && <ArtifactPanel />}
    </div>
  );
}
